import { RESTRICTED_BRANDS } from './restrictedBrandRegistry.ts';

export const RESTRICTED_BRAND_ERROR = 'BRAND_NOT_ALLOWED';

// These decorations do not turn a listed corporate brand into an own brand.
// Do not use substring/fuzzy matching: e.g. Pineapple and Monster Cookies are
// independent names. Domain matching never includes URL paths or query text.
const DECORATIONS = new Set([
  'the', 'official', 'offiziell', 'shop', 'store', 'online', 'onlineshop',
  'gmbh', 'ag', 'kg', 'ug', 'ohg', 'gbr', 'inc', 'incorporated', 'ltd',
  'limited', 'llc', 'co', 'corp', 'corporation', 'company', 'group', 'holding',
  'de', 'uk', 'us', 'usa', 'germany', 'deutschland', 'international',
]);

export function normalizeRestrictedBrandName(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.slice(0, 2048).normalize('NFKD').toLowerCase()
    .replace(/[\u0300-\u036f\u200b-\u200f\u202a-\u202e\u2060-\u206f\ufeff]/g, '')
    .replace(/ß/g, 'ss').replace(/[^a-z0-9]+/g, ' ').trim();
}

function nameKeys(value: unknown): string[] {
  const normalized = normalizeRestrictedBrandName(value);
  return [normalized.replace(/ /g, ''), normalized.split(' ').filter(token => !DECORATIONS.has(token)).join('')];
}

const blockedNames = new Set<string>(RESTRICTED_BRANDS.flatMap(brand =>
  [brand.name, ...brand.aliases].flatMap(nameKeys).filter(Boolean)));
const blockedDomains: readonly string[] = RESTRICTED_BRANDS.flatMap(brand => [...brand.domains]);

export function isRestrictedBrandName(value: unknown): boolean {
  return nameKeys(value).some(key => key.length > 0 && blockedNames.has(key));
}

export function isRestrictedBrandWebsite(value: unknown): boolean {
  if (typeof value !== 'string' || value.length > 2048) return false;
  const candidate = value.trim();
  if (!candidate) return false;
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(candidate) ? candidate : `https://${candidate.replace(/^\/+/, '')}`);
    const hostname = url.hostname.toLowerCase().replace(/\.$/, '');
    return blockedDomains.some(domain => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    return false; // URL validity is enforced separately, never treated as proof.
  }
}

export function isRestrictedCustomerBrand(input: {
  brandName?: unknown; companyName?: unknown; websiteUrl?: unknown; crawlUrl?: unknown;
}): boolean {
  return isRestrictedBrandName(input.brandName) || isRestrictedBrandName(input.companyName)
    || isRestrictedBrandWebsite(input.websiteUrl) || isRestrictedBrandWebsite(input.crawlUrl);
}

export function isRestrictedBrandError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { code?: unknown; message?: unknown };
  return candidate.code === RESTRICTED_BRAND_ERROR || candidate.message === RESTRICTED_BRAND_ERROR;
}
